// Pure rules of an API batch (G3, G6, G12; LH-F2 rows F2-4, F2-5). No React, no store: the screens and the store
// call these, the tests check them. Status goes one way: draft → tests passed → built → live; a live batch that is
// replaced becomes previous (kept for roll-back), an older previous becomes retired.
import { foldersOf, isKeep, starterFiles, testsPathOf } from '@/lib/endpoints'
import { staticDiscover } from '@/lib/manifest'
import type { Batch, BatchStatus, CodeFile, Manifest, TestEvent, TestResult } from '@/types'

export const STRIP: BatchStatus[] = ['draft', 'tests_passed', 'built', 'live']
export const STATUS_LABEL: Record<BatchStatus, string> = { draft: 'draft', tests_passed: 'tests passed', built: 'built', live: 'live', previous: 'previous', retired: 'retired' }

/** BP-1: what names a batch's code (git repo, bundle, sandbox, containers): its product, else its lender. Same as api2's batch-record.ts. */
export const codeKeyOf = (b: Pick<Batch, 'lenderSlug' | 'productSlug'>) => b.productSlug ?? b.lenderSlug
export const batchName = (b: Pick<Batch, 'lenderSlug' | 'productSlug' | 'version'>) => `${codeKeyOf(b)}.batchApi.${b.version}`
/** git: banxx.personal → banxx/batchApi-personal; banxx → banxx/batchApi (api2's git.ts repoOf) */
export function repoLabel(key: string) {
  const [org, product] = key.split('.')
  return `${org}/batchApi${product ? `-${product}` : ''}`
}
/** The product part of a product slug, for a label: banxx.personal → personal */
export const productShort = (productSlug: string) => productSlug.split('.').slice(1).join('.')

/**
 * BP-1: the batches a product runs on: its own, else (a lender from before) the lender's whole-lender batches. `productSlug`
 * null = the whole-lender batches only. Same as api2's batchesOfProduct.
 */
export function batchesOfProduct<B extends Pick<Batch, 'lenderSlug' | 'productSlug'>>(batches: B[], lenderSlug: string, productSlug: string | null) {
  const mine = batches.filter((b) => b.lenderSlug === lenderSlug)
  const own = productSlug ? mine.filter((b) => b.productSlug === productSlug) : []
  return own.length ? own : mine.filter((b) => !b.productSlug)
}
/** One batch code's versions: the product's own, or (null) the whole-lender ones. */
export const ofCode = <B extends Pick<Batch, 'lenderSlug' | 'productSlug'>>(batches: B[], lenderSlug: string, productSlug: string | null) =>
  batches.filter((b) => b.lenderSlug === lenderSlug && (b.productSlug ?? null) === productSlug)

/** Editable = a draft or a draft whose tests passed; built, live, previous and retired are read-only (G6). */
export const isEditable = (b: Batch) => b.status === 'draft' || b.status === 'tests_passed'
/** The same files with the same contents, in any order: the sandbox lists them by name, the Hub's copy in its own order. */
export const sameFiles = (a: { path: string; content: string }[], b: { path: string; content: string }[]) =>
  a.length === b.length && a.every((f) => b.some((g) => g.path === f.path && g.content === f.content))
export const canRunTests = (b: Batch) => isEditable(b) && b.tests.length > 0
/** Build needs: tests passed on the saved code (not dirty), and the code.publish permission (admins). */
export const canBuild = (b: Batch, can: (p: string) => boolean, dirty = false) => b.status === 'tests_passed' && !dirty && can('code.publish')
export const canMakeLive = (b: Batch, can: (p: string) => boolean) => b.status === 'built' && can('code.publish')
export const canRollBack = (b: Batch, can: (p: string) => boolean) => b.status === 'previous' && can('code.publish')

/** The "Next:" text of the status strip, for the person looking at it. */
export function nextStep(b: Batch, dirty: boolean, can: (p: string) => boolean): string {
  if (dirty) return 'Next: Save (one commit), then Run all tests'
  switch (b.status) {
    case 'draft':
      return b.testRun && b.testRun.passed < b.testRun.total ? `Next: fix the ${b.testRun.total - b.testRun.passed} red test${b.testRun.total - b.testRun.passed === 1 ? '' : 's'}, Save, Run all tests` : 'Next: Run all tests'
    case 'tests_passed':
      return can('code.publish') ? 'Next: Build (tag + bundle with checksum; the version is fixed)' : 'Next: an admin builds it'
    case 'built':
      return can('code.publish') ? 'Next: Make live (live workflows move to it after the C1 check)' : 'Next: an admin makes it live'
    case 'live':
      return 'Live and read-only. A change = New version from this.'
    case 'previous':
      return 'Kept for roll-back. Read-only.'
    case 'retired':
      return 'Retired. Read-only, kept for the record.'
  }
}

export function compareVersions(a: string, b: string): number {
  const pa = a.split('.').map(Number)
  const pb = b.split('.').map(Number)
  for (let i = 0; i < 3; i++) if ((pa[i] ?? 0) !== (pb[i] ?? 0)) return (pa[i] ?? 0) - (pb[i] ?? 0)
  return 0
}

/** The next minor version after the highest one of this lender: 1.3.0 → 1.4.0; none → 1.0.0. */
export function nextVersion(batches: Batch[]): string {
  const top = batches.map((b) => b.version).sort(compareVersions).at(-1)
  if (!top) return '1.0.0'
  const [maj, min] = top.split('.').map(Number)
  return `${maj}.${(min ?? 0) + 1}.0`
}

export const isValidVersion = (v: string) => /^\d+\.\d+\.\d+$/.test(v)

/** A short git-like sha for the fixture git log. */
export const shortSha = () => Math.floor(Math.random() * 0xfffffff).toString(16).padStart(7, '0')

/** SHA-256 of the files as the bundle checksum (Web Crypto; a plain hash when the browser lacks it). */
export async function checksumOf(files: CodeFile[]): Promise<string> {
  const text = files.slice().sort((a, b) => a.path.localeCompare(b.path)).map((f) => `${f.path}\n${f.content}`).join('\n---\n')
  const subtle = globalThis.crypto?.subtle
  if (subtle) {
    const digest = await subtle.digest('SHA-256', new TextEncoder().encode(text))
    return `sha256:${Array.from(new Uint8Array(digest)).map((b) => b.toString(16).padStart(2, '0')).join('')}`
  }
  let h = 0x811c9dc5
  for (let i = 0; i < text.length; i++) h = Math.imul(h ^ text.charCodeAt(i), 0x01000193) >>> 0
  return `fnv1a:${h.toString(16).padStart(8, '0')}`
}

/** Save = one commit; code changed, so "tests passed" falls back to draft (the tests must run again). */
export function applySave(b: Batch, files: CodeFile[], tests: TestEvent[], message: string, who: string, at: string, opts: { sha?: string; manifest?: Manifest } = {}): Batch {
  const { manifest } = opts
  if (!isEditable(b)) throw new Error(`${batchName(b)} is ${STATUS_LABEL[b.status]} and read-only; make a new version to change the code`)
  const changed = files.filter((f) => b.files.find((x) => x.path === f.path)?.content !== f.content).map((f) => f.path)
  const removed = b.files.filter((f) => !files.some((x) => x.path === f.path)).map((f) => f.path)
  const testsChanged = JSON.stringify(tests) !== JSON.stringify(b.tests)
  const touched = [...changed, ...removed, ...(testsChanged ? ['tests/'] : [])]
  const sha = opts.sha ?? shortSha()
  return {
    ...b,
    files,
    tests,
    manifest: manifest ?? staticDiscover(files, at),
    status: 'draft',
    git: { ...b.git, commit: sha },
    commits: [{ sha, message: message.trim() || `Update ${touched.join(', ') || 'files'}`, who, at, files: touched }, ...b.commits],
    updatedAt: at,
  }
}

/** Run all tests: all green on an editable batch → tests passed; any red → stays draft. */
export function applyTestRun(b: Batch, results: TestResult[], who: string, at: string): Batch {
  const passed = results.filter((r) => r.ok).length
  const testRun = { passed, total: results.length, at, by: who }
  if (!isEditable(b)) return { ...b, testRun }
  return { ...b, testRun, status: passed === results.length && results.length > 0 ? 'tests_passed' : 'draft', updatedAt: at }
}

/** Build (admin, after tests passed): git tag + bundle in storage with checksum; from here the version is fixed. */
export function applyBuild(b: Batch, checksum: string, who: string, at: string): Batch {
  if (b.status !== 'tests_passed') throw new Error(`Build needs "tests passed"; ${batchName(b)} is ${STATUS_LABEL[b.status]}`)
  return {
    ...b,
    status: 'built',
    git: { ...b.git, tag: `${repoLabel(codeKeyOf(b))}/${b.version}` },
    bundle: { key: `gw-bundles/${codeKeyOf(b)}/${b.version}.tgz`, checksum, builtAt: at, builtBy: who },
    updatedAt: at,
  }
}

export interface LiveChange {
  batches: Batch[]
  /** the version that was live before, now previous */
  previous: string | null
  /** the version that was previous before, now retired */
  retired: string | null
}

/**
 * Make live (admin, after build): the batch becomes live; the live batch of the same code (BP-1: the same product, or the
 * whole-lender batch) becomes previous, its previous
 * becomes retired. The live workflows that follow the live batch move with it; the caller runs the C1 check first
 * (src/lib/flow.ts c1Check) and refuses when a workflow would break.
 */
export function applyMakeLive(all: Batch[], id: string, at: string): LiveChange {
  const target = all.find((b) => b.id === id)
  if (!target) throw new Error('batch not found')
  if (target.status !== 'built') throw new Error(`Make live needs "built"; ${batchName(target)} is ${STATUS_LABEL[target.status]}`)
  const same = (b: Batch) => b.lenderSlug === target.lenderSlug && codeKeyOf(b) === codeKeyOf(target)
  const oldLive = all.find((b) => same(b) && b.status === 'live')
  const oldPrevious = all.find((b) => same(b) && b.status === 'previous')
  const batches = all.map((b) => {
    if (b.id === id) return { ...b, status: 'live' as const, madeLiveAt: at, updatedAt: at }
    if (oldLive && b.id === oldLive.id) return { ...b, status: 'previous' as const, updatedAt: at }
    if (oldPrevious && b.id === oldPrevious.id) return { ...b, status: 'retired' as const, updatedAt: at }
    return b
  })
  return { batches, previous: oldLive?.version ?? null, retired: oldPrevious?.version ?? null }
}

/** Roll back (admin): the previous version is live again; the current live one becomes previous; workflows move back. */
export function applyRollBack(all: Batch[], id: string, at: string): LiveChange {
  const target = all.find((b) => b.id === id)
  if (!target) throw new Error('batch not found')
  if (target.status !== 'previous') throw new Error(`Only a "previous" version can be rolled back to; ${batchName(target)} is ${STATUS_LABEL[target.status]}`)
  const live = all.find((b) => b.lenderSlug === target.lenderSlug && codeKeyOf(b) === codeKeyOf(target) && b.status === 'live')
  const batches = all.map((b) => {
    if (b.id === id) return { ...b, status: 'live' as const, madeLiveAt: at, updatedAt: at }
    if (live && b.id === live.id) return { ...b, status: 'previous' as const, updatedAt: at }
    return b
  })
  return { batches, previous: live?.version ?? null, retired: null }
}

/** New version from this: a copy of the files and tests as a draft on its own git branch (BP-1: of one product's batch). */
export function newVersionFrom(from: Batch | null, lenderSlug: string, version: string, who: string, at: string, productSlug: string | null = from?.productSlug ?? null): Batch {
  if (!isValidVersion(version)) throw new Error('The version number must look like 1.2.0')
  const sha = shortSha()
  const key = productSlug ?? lenderSlug
  return {
    id: `b_${key.replace(/\./g, '_')}_${version.replace(/\./g, '')}_${Date.now().toString(36)}`,
    lenderSlug,
    productSlug,
    version,
    status: 'draft',
    madeFrom: from?.version ?? null,
    files: from ? JSON.parse(JSON.stringify(from.files)) : starterFiles(key, version),
    manifest: from ? JSON.parse(JSON.stringify(from.manifest)) : staticDiscover([], at),
    tests: from ? JSON.parse(JSON.stringify(from.tests)) : [],
    testRun: null,
    git: { branch: `${repoLabel(key)}/${version}`, tag: null, commit: sha },
    bundle: null,
    commits: [{ sha, message: from ? `New version from ${from.version}` : 'New batch', who, at, files: [] }],
    createdBy: who,
    createdAt: at,
    updatedAt: at,
    madeLiveAt: null,
  }
}

export interface TreeNode {
  /** the file path, a folder path, or a virtual tests/… path */
  path: string
  name: string
  /** closed = a folder of the sandbox the editor does not open (node_modules, .hub) */
  kind: 'folder' | 'file' | 'tests-folder' | 'tests' | 'closed'
  children?: TreeNode[]
}

/**
 * The playground's explorer: the batch's real folders and files (any depth; `.gitkeep` keeps an empty folder and is
 * not shown), folders first, then a virtual tests/ folder with one generated file per endpoint that has test events.
 * `extra` = entries the sandbox has that the store does not (node_modules, .hub), shown closed.
 */
export function fileTree(files: { path: string }[], tests: TestEvent[], endpoints: { path: string }[] = [], extra: { path: string; count?: number }[] = []): TreeNode[] {
  const root: TreeNode[] = []
  const folderNode = new Map<string, TreeNode>()
  const childrenOf = (folder: string) => (folder ? folderNode.get(folder)!.children! : root)
  for (const folder of foldersOf(files)) {
    const parent = folder.includes('/') ? folder.slice(0, folder.lastIndexOf('/')) : ''
    const node: TreeNode = { path: folder, name: folder.split('/').pop()!, kind: 'folder', children: [] }
    folderNode.set(folder, node)
    childrenOf(parent).push(node)
  }
  for (const f of files) {
    if (isKeep(f.path)) continue
    const folder = f.path.includes('/') ? f.path.slice(0, f.path.lastIndexOf('/')) : ''
    childrenOf(folder).push({ path: f.path, name: f.path.split('/').pop()!, kind: 'file' })
  }
  for (const x of extra) root.push({ path: x.path, name: `${x.path}${x.count !== undefined ? ` (${x.count})` : ''}`, kind: 'closed' })
  const sort = (list: TreeNode[]) => {
    const rank = (n: TreeNode) => (n.kind === 'folder' ? 0 : n.kind === 'file' ? 1 : 2)
    list.sort((a, b) => rank(a) - rank(b) || a.name.localeCompare(b.name))
    for (const n of list) if (n.children) sort(n.children)
  }
  sort(root)
  const routes = endpoints.map((e) => e.path).filter((r) => tests.some((t) => t.endpoint === r))
  if (routes.length) root.push({ path: 'tests', name: 'tests', kind: 'tests-folder', children: routes.map((r) => ({ path: testsPathOf(r), name: testsPathOf(r).slice(6), kind: 'tests' as const })) })
  return root
}

/** The virtual tests/<endpoint>.replies.json content: the test events of that endpoint, as the git container will store them. */
export function testsJson(tests: TestEvent[], route: string): string {
  return JSON.stringify(tests.filter((x) => x.endpoint === route).map(({ name, reply, expectedOutcome, source }) => ({ name, reply, expected: expectedOutcome, source })), null, 2) + '\n'
}
